import { useEffect, useRef } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";

import { useCurrentWorkspace } from "src/api/queries";
import type { CopilotResourceRef } from "src/clients/agent-management";
import { type DashboardPageParams } from "src/router/urls";
import {
  CONTEXTUAL_ENTITY_DEFINITIONS,
  isReferenceContextualEntityType,
} from "src/unifiedCopilot/context/entityDefinitions";
import { resolveEntityRefChain } from "src/unifiedCopilot/context/entityResolution";
import { useContextualEntities } from "src/unifiedCopilot/context/internalHooks";
import type {
  ContextualEntityDeeplink,
  ContextualEntityDeeplinkContext,
} from "src/unifiedCopilot/context/types";
import { toastError } from "src/utils/toastError";
import { useParamsDecode } from "src/utils/useParamsDecode";

/** What a ref points at, for effect deps that must not follow its identity. */
const refIdentity = (ref: CopilotResourceRef | null): string | null =>
  ref ? `${ref.type}:${ref.id}:${ref.parent?.id ?? ""}` : null;

/**
 * What a mention leads to: the type's action on the ref as it came, and on its
 * completed ancestor chain when the ref alone was not enough. Resolution is
 * skipped for a ref that already satisfies its deeplink (same-page node
 * selection, a chain carried on the mention).
 *
 * The click and the warm share this one path, so every query the click reads —
 * the type's own, and each one resolution walks through — is a query the warm
 * has already touched.
 */
const deeplinkCallback = async (
  ref: CopilotResourceRef,
  deeplink: ContextualEntityDeeplink,
  context: ContextualEntityDeeplinkContext,
): Promise<(() => void) | undefined> =>
  deeplink.action(ref, context) ??
  deeplink.action(await resolveEntityRefChain(ref, context), context);

/**
 * Calls `warm` once per ref, and again once it becomes possible at all (the
 * workspace query landing turns it from undefined into a callback). Held in a
 * ref because it closes over a deeplink context that is a new object every
 * render, while what it reads depends on the ref alone.
 */
const useWarmDeeplink = (
  warm: (() => void) | undefined,
  refKey: string | null,
): void => {
  const latestWarm = useRef(warm);
  latestWarm.current = warm;
  const canWarm = warm !== undefined;
  useEffect(() => {
    latestWarm.current?.();
  }, [refKey, canWarm]);
};

/**
 * The click side effect for a resource mention pill: resolve the ref's
 * ancestor chain (in-memory sources first, the type's resolver when uncached)
 * and follow the type's deeplink. Returns `undefined` when the ref cannot
 * possibly resolve (unknown or inert type, a missing parent, no workspace
 * yet) — the pill then stays inert.
 *
 * The whole path runs once while the pill renders, with its callback thrown
 * away, so both the type's own query and any ancestor the chain has to
 * discover are already warm by the time anyone clicks.
 *
 * Navigation stays in-tab: the copilot window lives outside the routed page
 * tree and survives the route change.
 */
export const useResourceMentionClick = (
  ref: CopilotResourceRef | null,
): (() => void) | undefined => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const entities = useContextualEntities();
  const queryClient = useQueryClient();
  // Read through the query rather than the route outlet context: the pill
  // renders inside the copilot window, which sits outside the routed page
  // tree. Undefined only until the workspace query lands.
  const { data: workspace } = useCurrentWorkspace();

  const definition =
    ref && isReferenceContextualEntityType(ref.type)
      ? CONTEXTUAL_ENTITY_DEFINITIONS[ref.type]
      : undefined;
  const deeplink = definition?.deeplink;
  // A type that is only addressable within a parent needs that parent on the
  // mention; nothing else could make the ref resolve.
  const hasParent =
    !definition?.parentType || ref?.parent?.type === definition.parentType;

  const context: ContextualEntityDeeplinkContext | null =
    ref && deeplink && orgId && wsId && workspace && hasParent
      ? { orgId, wsId, workspace, entities, queryClient, pathname, navigate }
      : null;

  // Run for the warming side effect alone; the callback is thrown away, and a
  // failure here is left to the click, which reports its own.
  useWarmDeeplink(
    ref && deeplink && context
      ? () =>
          void deeplinkCallback(ref, deeplink, context).catch(() => undefined)
      : undefined,
    refIdentity(ref),
  );

  if (!context || !ref || !deeplink) {
    return undefined;
  }
  return () =>
    void (async () => {
      try {
        const onClick = await deeplinkCallback(ref, deeplink, context);
        if (!onClick) {
          toastError(new Error(`${ref.type} ${ref.id} was not found`), {
            title: "Failed to open resource",
          });
          return;
        }
        onClick();
      } catch (error) {
        toastError(error, { title: "Failed to open resource" });
      }
    })();
};
